import { useQuery } from '@tanstack/react-query';

import { getEngagementIntake } from '@/api/portalIntake';
import { useAuth } from '@/auth/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { answerLabel, groupBySection } from '@/lib/portalIntake';

/** Practitioner-side, read-only view of the customer's intake
 *  questionnaire: answers grouped by section plus the submit state. */
export function EngagementIntakePanel({ engagementId }: { engagementId: string }) {
  const { userKey, getIdToken } = useAuth();

  const intake = useQuery({
    queryKey: ['engagementIntake', engagementId, userKey],
    queryFn: async () => getEngagementIntake(engagementId, (await getIdToken()) ?? ''),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Intake questionnaire</CardTitle>
        <CardDescription>
          {intake.data == null
            ? 'What the customer answered during onboarding.'
            : intake.data.submitted_at
              ? `Marked complete by the customer on ${new Date(intake.data.submitted_at).toLocaleString()}.`
              : 'Not yet marked complete by the customer.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {intake.isPending ? (
          <p className="text-sm text-brand-slate">Loading questionnaire…</p>
        ) : intake.isError ? (
          <p className="text-sm text-destructive">Couldn&apos;t load the questionnaire.</p>
        ) : (
          <dl className="grid gap-4">
            {groupBySection(intake.data.intake_schema.questions).map((section) => (
              <div key={section.section} className="grid gap-2">
                <h4 className="text-sm font-medium text-brand-navy">{section.section}</h4>
                {section.questions.map((question) => (
                  <div
                    key={question.id}
                    className="grid gap-0.5 rounded-md border border-border px-3 py-2"
                  >
                    <dt className="text-xs text-brand-slate">{question.prompt}</dt>
                    <dd className="text-sm text-brand-navy">
                      {answerLabel(question, intake.data.answers[question.id])}
                    </dd>
                  </div>
                ))}
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
